<template>
  <MainLayout>
    <template #header>
      <div class="flex justify-end gap-2">
        <ContactImportButton contact-type="Candidate" />
        <ContactExportButton contact-type="Candidate" />
        <ContactAddButton contact-type="Candidate" />
      </div>
    </template>
    <template #subheader> Track and manage your candidate pipeline </template>
    <div class="flex gap-2 justify-between pb-4">
      <div class="flex flex-wrap gap-2 items-center">
        <CandidateSearch @search-changed="getCandidates" />
        <CandidateFilters @filter-changed="getCandidates" />
      </div>
      <div>
        <Button
          size="icon"
          class="cursor-pointer text-gray-500"
          :variant="displayVariant('kanban')"
          @click="setView('kanban')"
        >
          <vue-feather type="grid" size="20" />
        </Button>
        <Button
          size="icon"
          class="cursor-pointer text-gray-500"
          :variant="displayVariant('list')"
          @click="setView('list')"
        >
          <vue-feather type="list" size="20" />
        </Button>
      </div>
    </div>

    <div v-if="displayType === 'kanban'">
      <KanbanBoard :columns="kanbanData" :base-url="baseUrl" @load-more="getCandidates" />
    </div>
    <div v-else-if="displayType === 'list'">
      <CandidateList
        :items="candidateListData"
        :total="totalCandidates"
        :page="listPage"
        @get-candidates="getCandidates"
      />
    </div>
    <p class="text-gray-600 text-sm">Showing {{ candidateListData.length }} of {{ totalCandidates }} candidates</p>
  </MainLayout>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, provide } from 'vue';
import { hFetch } from '@/lib/hfetch';
import MainLayout from '@/components/layouts/MainLayout/index.vue';
import KanbanBoard from '@/components/shared/Kanban/KanbanBoard.vue';
import CandidateList from '@/components/recruiting/Candidates/CandidateList.vue';
import type { KanbanColumnData, FetchParams } from '@/types/kanban';
import { useLoadingSpinnerStore } from '@/stores/loadingSpinner.ts';
import { Button } from '@/components/ui/button';
import { toast } from 'vue-sonner';
import { useRoute, useRouter } from 'vue-router';
import CandidateSearch from '@/components/recruiting/Candidates/CandidateSearch.vue';
import CandidateFilters from '@/components/recruiting/Candidates/CandidateFilters.vue';
import { queryToEnum } from '@/lib/typeParser';
import { sortOptions, displayOptions, orderOptions } from '@/api/entity_types/recruiting/Candidate';
import type { DisplayOption } from '@/api/entity_types/recruiting/Candidate';
import ContactImportButton from '@/components/contact/ContactImportButton.vue';
import ContactExportButton from '@/components/contact/ContactExportButton.vue';
import ContactAddButton from '@/components/contact/ContactAddButton.vue';
import { candidateFilterOptions } from '@/api/entity_types/recruiting/CandidateFilters';
import RailsUtility from '@/utilities/rails';
import type { LicenseStatusLabel } from '@/types/LicenseStatusLabel';

const route = useRoute();
const router = useRouter();
const loadingSpinnerStore = useLoadingSpinnerStore();
const baseUrl = '/api/recruiting/candidates';

onMounted(async () => {
  loadingSpinnerStore.showLocalLoadingSpinner();
  await setFiltersFromUrl();
  await getCandidates();
  loadingSpinnerStore.hideLocalLoadingSpinner();
});

const urlData = computed(() => {
  return {
    view: queryToEnum(route.query.view, displayOptions),
    sort: queryToEnum(route.query.sort, sortOptions),
    order: queryToEnum(route.query.order, orderOptions),
  };
});

const kanbanData = ref<KanbanColumnData[]>([]);
const displayType = ref(urlData.value.view || 'kanban');
const listPage = ref<number>(1);
const totalCandidates = ref<number>(0);
const filters = ref<{ field: string; value: string }[]>([]);
const search = ref<{ field: string; value: string } | null>(null);

provide('refreshCandidates', getCandidates);

const candidateListData = computed(() => {
  return kanbanData.value
    .flatMap(col => {
      return col.tasks.map(task => ({ ...task, stage: col.title }));
    })
    .sort((a, b) => a.rownum - b.rownum);
});

function displayVariant(view: DisplayOption) {
  return displayType.value === view ? 'secondary' : 'ghost';
}

async function setView(view: DisplayOption) {
  displayType.value = view;
  const queryParams = { ...route.query };
  queryParams['view'] = view;
  await router.push({ query: queryParams });
  await getCandidates();
}

async function setFiltersFromUrl() {
  candidateFilterOptions.forEach(filter => {
    const filter_value = route.query[filter.id]?.toString().toLowerCase();
    if (filter_value) {
      const validOptions = filter.options.map(option => option.id.toString());
      if (validOptions.includes(filter_value)) {
        filters.value.push({ field: filter.id, value: filter_value });
      }
    }
  });
}

async function getCandidates(fetchParams?: FetchParams) {
  const page = fetchParams?.page || 1;
  const params = getParams(fetchParams);

  const url = `${baseUrl}?${params.toString()}`;
  const response = await hFetch<{ total: number; stages: KanbanColumnData[] }>({ url: url });

  if (response.status === 'success') {
    const parsedColumnData = parseColumnData(response.data.stages);

    if (page === 1) {
      kanbanData.value = parsedColumnData;
      totalCandidates.value = response.data.total;
    } else {
      addAdditionalKanbanRows(parsedColumnData);
    }
    listPage.value = page;
  } else {
    toast.error('Error fetching candidates', {
      description: response.messages.join(', '),
    });
  }
}

function addAdditionalKanbanRows(stages: KanbanColumnData[]) {
  for (let stage of stages) {
    if (stage.tasks.length > 0) {
      const column = kanbanData.value.find(col => col.column_id === stage.column_id);
      if (!column) continue;
      {
        column.tasks.push(...stage.tasks);
        column.pagination = stage.pagination;
      }
    }
  }
}

function getParams(fetchParams?: FetchParams) {
  const { sort, order } = urlData.value;

  if (fetchParams?.filters) {
    filters.value = fetchParams.filters;
  }
  if (fetchParams?.search) {
    search.value = fetchParams.search;
  }

  const params = new URLSearchParams();
  params.append('page', (fetchParams?.page || 1).toString());
  if (fetchParams?.column_id) params.append('column_id', fetchParams.column_id.toString());
  if (fetchParams?.last_index) params.append('last_index', fetchParams.last_index.toString());
  if (search.value?.field) params.append('search_field', search.value.field);
  if (search.value?.value) params.append('search_value', search.value.value);
  if (sort) params.append('sort', sort);
  if (order) params.append('order', order);
  if (displayType.value) params.append('view', displayType.value);
  filters.value?.forEach(filter => {
    params.append(filter.field, filter.value);
  });

  return params;
}

function parseColumnData(stages: KanbanColumnData[]) {
  return stages.map(stage => ({
    ...stage,
    tasks: stage.tasks.map(task => ({
      ...task,
      license_status: RailsUtility.convertFromCasedEnumFormat(task.license_status) as LicenseStatusLabel,
    })),
  }));
}
</script>
